import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function supportsWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch { return false; }
}

function buildScene(canvas, reduced) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(clamp(window.devicePixelRatio || 1, 1, 1.75));
  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x0a1628, 0.055);
  const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
  camera.position.set(0, 1.6, 9);

  const blue = new THREE.Color(0x1a6eff);
  const cyan = new THREE.Color(0x00d4ff);
  const disposables = [];

  // Perspective grid floor
  const grid = new THREE.GridHelper(60, 48, 0x1a6eff, 0x1a6eff);
  grid.material.transparent = true; grid.material.opacity = 0.22;
  grid.position.y = -2.2;
  scene.add(grid); disposables.push(grid.geometry, grid.material);

  // Central hexagonal core (brand echo)
  const coreGeo = new THREE.TorusGeometry(1.6, 0.06, 6, 6);
  const coreMat = new THREE.MeshBasicMaterial({ color: blue, transparent: true, opacity: 0.9 });
  const core = new THREE.Mesh(coreGeo, coreMat); core.rotation.x = Math.PI / 2.4;
  const core2 = new THREE.Mesh(new THREE.TorusGeometry(2.2, 0.035, 6, 6), new THREE.MeshBasicMaterial({ color: cyan, transparent: true, opacity: 0.6 }));
  core2.rotation.x = Math.PI / 2.4; core2.rotation.z = Math.PI / 6;
  const nucleus = new THREE.Mesh(new THREE.SphereGeometry(0.42, 32, 32), new THREE.MeshBasicMaterial({ color: blue }));
  const group = new THREE.Group(); group.add(core, core2, nucleus); group.position.y = 0.3;
  scene.add(group);
  disposables.push(coreGeo, coreMat, core2.geometry, core2.material, nucleus.geometry, nucleus.material);

  // Floating geometric forms
  const forms = [];
  const formGeos = [new THREE.OctahedronGeometry(0.35), new THREE.IcosahedronGeometry(0.3), new THREE.TetrahedronGeometry(0.32)];
  for (let i = 0; i < 9; i++) {
    const geo = formGeos[i % 3];
    const mat = new THREE.MeshBasicMaterial({ color: i % 2 ? cyan : blue, wireframe: true, transparent: true, opacity: 0.55 });
    const m = new THREE.Mesh(geo, mat);
    const a = (i / 9) * Math.PI * 2;
    m.position.set(Math.cos(a) * (3.2 + (i % 3) * 0.6), Math.sin(a * 1.7) * 1.4 + 0.4, Math.sin(a) * 2.2);
    m.userData = { a, r: 3.2 + (i % 3) * 0.6, speed: 0.12 + (i % 4) * 0.03 };
    scene.add(m); forms.push(m); disposables.push(mat);
  }
  disposables.push(...formGeos);

  // Particles / data streams
  const N = 900;
  const pos = new Float32Array(N * 3); const seed = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    pos[i * 3] = (Math.random() - 0.5) * 22; pos[i * 3 + 1] = (Math.random() - 0.5) * 9 + 0.5; pos[i * 3 + 2] = (Math.random() - 0.5) * 16;
    seed[i] = Math.random() * Math.PI * 2;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  const pMat = new THREE.PointsMaterial({ color: cyan, size: 0.035, transparent: true, opacity: 0.8, sizeAttenuation: true });
  const points = new THREE.Points(pGeo, pMat); scene.add(points); disposables.push(pGeo, pMat);

  // Orbs (soft glowing spheres)
  const orbs = [];
  for (let i = 0; i < 4; i++) {
    const mat = new THREE.MeshBasicMaterial({ color: i % 2 ? cyan : blue, transparent: true, opacity: 0.18 });
    const o = new THREE.Mesh(new THREE.SphereGeometry(0.5 + i * 0.18, 24, 24), mat);
    o.position.set(-4 + i * 2.7, 1.8 - (i % 2) * 2.4, -3 - i);
    o.userData = { base: o.position.clone(), phase: i * 1.3 };
    scene.add(o); orbs.push(o); disposables.push(o.geometry, mat);
  }

  const pointer = { x: 0, y: 0 }; const target = { x: 0, y: 0 };
  let raf = 0; let running = true; const clock = new THREE.Clock();

  const resize = () => {
    const w = canvas.clientWidth || 1; const h = canvas.clientHeight || 1;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  };
  resize();

  const render = () => {
    if (!running) return;
    const t = clock.getElapsedTime(); const dt = reduced ? 0 : 1;
    target.x += (pointer.x - target.x) * 0.05; target.y += (pointer.y - target.y) * 0.05;
    camera.position.x = target.x * 0.9; camera.position.y = 1.6 + target.y * 0.5; camera.lookAt(0, 0.3, 0);
    group.rotation.y = t * 0.25 * dt; core2.rotation.y = -t * 0.35 * dt; nucleus.scale.setScalar(1 + Math.sin(t * 2) * 0.05 * dt);
    forms.forEach((m, i) => {
      const a = m.userData.a + t * m.userData.speed * dt;
      m.position.x = Math.cos(a) * m.userData.r; m.position.z = Math.sin(a) * 2.2;
      m.position.y = clamp(Math.sin(a * 1.7) * 1.4 + 0.4, -1.8, 3);
      m.rotation.x += 0.004 * dt; m.rotation.y += 0.006 * dt;
    });
    orbs.forEach((o) => { o.position.y = o.userData.base.y + Math.sin(t * 0.5 + o.userData.phase) * 0.35 * dt; });
    const p = pGeo.attributes.position.array;
    for (let i = 0; i < N; i++) {
      let z = p[i * 3 + 2] + 0.012 * dt; if (z > 8) z = -8; p[i * 3 + 2] = z;
      p[i * 3 + 1] += Math.sin(t + seed[i]) * 0.0015 * dt;
    }
    pGeo.attributes.position.needsUpdate = true;
    grid.position.z = (t * 0.6 * dt) % 1.25;
    renderer.render(scene, camera);
    raf = requestAnimationFrame(render);
  };
  render();

  const onMove = (e) => {
    const r = canvas.getBoundingClientRect();
    pointer.x = clamp(((e.clientX - r.left) / r.width - 0.5) * 2, -1, 1);
    pointer.y = clamp(-((e.clientY - r.top) / r.height - 0.5) * 2, -1, 1);
  };
  const onVis = () => { running = !document.hidden; if (running) render(); else cancelAnimationFrame(raf); };
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);
  if (!reduced && window.matchMedia("(pointer: fine)").matches) window.addEventListener("pointermove", onMove);
  document.addEventListener("visibilitychange", onVis);

  return () => {
    running = false; cancelAnimationFrame(raf);
    ro.disconnect(); window.removeEventListener("pointermove", onMove); document.removeEventListener("visibilitychange", onVis);
    disposables.forEach((d) => d.dispose && d.dispose());
    scene.clear(); renderer.dispose();
  };
}

export function HeroFallback() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-x-0 bottom-0 h-[70%] perspective-grid" />
      <div className="absolute left-[15%] top-[20%] h-40 w-40 rounded-full bg-[#1A6EFF]/30 blur-3xl orb-drift" />
      <div className="absolute right-[15%] top-[35%] h-56 w-56 rounded-full bg-[#00D4FF]/20 blur-3xl float-diagonal" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-40 w-40 rounded-[28%] border-2 border-[#1A6EFF]/60 rotate-slow glow-blue" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-[#1A6EFF] glow-cyan float-y" />
    </div>
  );
}

export default function HeroScene({ className = "" }) {
  const ref = useRef(null);
  const [ok, setOk] = useState(true);
  useEffect(() => {
    if (!supportsWebGL() || !ref.current) { setOk(false); return; }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = document.createElement("canvas");
    canvas.className = "absolute inset-0 h-full w-full";
    canvas.setAttribute("data-testid", "hero-webgl-canvas");
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", "Abstract 3D visualization of connected data, intelligence and technology nodes");
    ref.current.appendChild(canvas);
    let dispose;
    try { dispose = buildScene(canvas, reduced); } catch (e) { console.warn("WebGL scene failed, using fallback", e); setOk(false); }
    return () => { dispose && dispose(); canvas.remove(); };
  }, []);
  if (!ok) return <HeroFallback />;
  return <div ref={ref} className={`absolute inset-0 ${className}`} />;
}
